'use client';

import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';

type ProductOption = {
  id: string;
  name: string;
  slug: string;
  category: string;
  href: string;
};

export function TemplateProductChooser({
  products,
  locale,
  templateId,
  defaultProductId,
}: {
  products: ProductOption[];
  locale: string;
  templateId: string;
  defaultProductId: string;
}) {
  const copy = {
    nl: { search: 'Zoek product', searching: 'Zoeken...', empty: 'Geen producten met designer gevonden.', defaultProduct: 'Standaard product' },
    de: { search: 'Produkt suchen', searching: 'Suchen...', empty: 'Keine Produkte mit Designer gefunden.', defaultProduct: 'Standardprodukt' },
    en: { search: 'Search product', searching: 'Searching...', empty: 'No products with designer found.', defaultProduct: 'Default product' },
    fr: { search: 'Rechercher un produit', searching: 'Recherche...', empty: 'Aucun produit avec designer trouvé.', defaultProduct: 'Produit par défaut' },
  }[locale as 'nl' | 'de' | 'en' | 'fr'] || { search: 'Zoek product', searching: 'Zoeken...', empty: 'Geen producten met designer gevonden.', defaultProduct: 'Standaard product' };
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ProductOption[]>(products);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults(products);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/products/search?q=${encodeURIComponent(trimmed)}&locale=${encodeURIComponent(locale)}&designerOnly=1`, {
          signal: controller.signal,
        });
        const data = await response.json().catch(() => null);
        setResults(Array.isArray(data?.products) ? data.products : []);
      } catch (error) {
        if (!controller.signal.aborted) setResults([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 180);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [locale, products, query]);

  const orderedResults = useMemo(() => {
    return [...results].sort((a, b) => {
      if (a.id === defaultProductId) return -1;
      if (b.id === defaultProductId) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [defaultProductId, results]);

  const localizedHref = (href: string) => {
    const normalizedHref = href.startsWith('/') ? href : `/${href}`;
    return normalizedHref.startsWith(`/${locale}/`) ? normalizedHref : `/${locale}${normalizedHref}`;
  };

  return (
    <div className="space-y-3">
      <label className="relative block">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={copy.search}
          className="h-11 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          autoFocus
        />
      </label>
      <div className="max-h-[52vh] space-y-2 overflow-y-auto pr-1">
        {loading && <p className="px-1 text-sm text-slate-500">{copy.searching}</p>}
        {!loading && orderedResults.length === 0 && (
          <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
            {copy.empty}
          </p>
        )}
        {orderedResults.map((product) => {
          const isDefault = product.id === defaultProductId;
          const href = `${localizedHref(product.href)}?designTemplate=${encodeURIComponent(templateId)}#configurator`;
          return (
            <a
              key={product.id}
              href={href}
              className={`block rounded-lg border px-3 py-3 transition ${
                isDefault
                  ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-100'
                  : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-slate-50'
              }`}
            >
              <span className="block font-bold text-slate-950">{product.name}</span>
              <span className="text-xs font-semibold text-slate-500">
                {isDefault ? copy.defaultProduct : product.category}
              </span>
            </a>
          );
        })}
      </div>
    </div>
  );
}
